import { z } from 'zod';
import { ClientProjectActivityStatusSchema } from './client.js';
import { InvoiceStatusSchema } from './invoice.js';
import { SupplierInvoiceStatusSchema } from './supplier-invoice.js';
import { SupplierInvoiceExtractionStatusSchema } from './supplier-invoice-extraction.js';
// =============================================================================
// Account overview (super admin)
//
// The cross-domain activity snapshot behind the super-admin account view: how
// many of each record kind exist on one account, how they are distributed
// across each domain's status enum, and when the most recent one landed.
//
// This lives in its own file rather than in `account.ts` to avoid an import
// cycle: `client.ts` already imports `account.ts`, so pulling the status enums
// into `account.ts` would close the loop and leave the zod consts partially
// initialised at module-load time.
// =============================================================================
/**
 * Every block below carries `byStatus` with a bucket for each value of the
 * owning domain's status enum - the zero buckets included, so the client renders
 * a stable set of chips without knowing the enum itself - plus a `total` and the
 * newest `createdAt`, which is null when the account has no records of that kind.
 *
 * The four blocks are spelled out rather than produced by a shared generic
 * factory: zod's `record` key parameter is not expressible through a plain
 * `z.ZodType` type parameter, and the workarounds cost more clarity than the
 * repetition does.
 */
export const AccountOverviewSchema = z.object({
    // Archived rows sit in their own bucket rather than folded into their last
    // live status, so "300 logged" reads differently from "300 logged, 280 since
    // archived".
    activities: z.object({
        byStatus: z.record(ClientProjectActivityStatusSchema, z.number()),
        archived: z.number(),
        total: z.number(),
        latestCreatedAt: z.string().nullable(),
    }),
    invoices: z.object({
        byStatus: z.record(InvoiceStatusSchema, z.number()),
        archived: z.number(),
        total: z.number(),
        latestCreatedAt: z.string().nullable(),
    }),
    // Expenses have no soft-delete: a retired expense is VOIDED, so there is no
    // archived bucket to report.
    expenses: z.object({
        byStatus: z.record(SupplierInvoiceStatusSchema, z.number()),
        total: z.number(),
        latestCreatedAt: z.string().nullable(),
    }),
    extractions: z.object({
        byStatus: z.record(SupplierInvoiceExtractionStatusSchema, z.number()),
        total: z.number(),
        latestCreatedAt: z.string().nullable(),
    }),
    payments: z.object({ total: z.number(), latestCreatedAt: z.string().nullable() }),
    chat: z.object({
        conversations: z.number(),
        messages: z.number(),
        latestActiveAt: z.string().nullable(),
    }),
    clients: z.object({ total: z.number() }),
    clientProjects: z.object({ total: z.number() }),
    memberCount: z.number(),
    /**
     * Newest timestamp across every block above; null on an account with no
     * records at all. This is the closest signal the operational store has to
     * "when was someone last working in here" - actual sign-in events
     * (`auth.user.logged-in`) are published to the BigQuery analytics stream and
     * never persisted to Postgres, so a true login history cannot be served from
     * this endpoint.
     */
    lastRecordAt: z.string().nullable(),
});
